import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import axios from 'axios'
import { trackPropertySaved } from '@/lib/analytics/propertyEvents'
import { webApi } from '@/services/webApi'
import { normalizeProperty } from '@/lib/apiNormalize'
import { useAuthStore } from '@/store/authStore'
import type { Property, PropertyIntent } from '@/types/property'

export interface WishlistToggleInput {
  propertyId: number
  source?: string
  cityId?: number
  areaId?: number
  categoryId?: number
  intent?: PropertyIntent
}

export const WISHLIST_IDS_QUERY_KEY = ['web', 'wishlist', 'ids'] as const
export const WISHLIST_LIST_QUERY_KEY = ['web', 'wishlist', 'list'] as const

export function useWishlistIds() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: WISHLIST_IDS_QUERY_KEY,
    queryFn: () => webApi.wishlistIds(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  })
}

export function useIsPropertyLiked(propertyId: number) {
  const query = useWishlistIds()
  const liked = query.data?.ids.includes(propertyId) ?? false

  return {
    liked,
    isLoading: query.isLoading,
  }
}

export function useWishlistProperties(page = 1) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const query = useQuery({
    queryKey: [...WISHLIST_LIST_QUERY_KEY, page],
    queryFn: () => webApi.wishlist({ page, perPage: 12 }),
    enabled: isAuthenticated,
  })

  const items: Property[] = (query.data?.items ?? []).map(normalizeProperty)

  return {
    ...query,
    items,
    pagination: query.data
      ? {
          currentPage: query.data.currentPage,
          lastPage: query.data.lastPage,
          perPage: query.data.perPage,
          total: query.data.total,
        }
      : null,
  }
}

export function useToggleWishlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: WishlistToggleInput) => webApi.toggleWishlist(input.propertyId),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: WISHLIST_IDS_QUERY_KEY })
      const previous = queryClient.getQueryData<{ ids: number[] }>(WISHLIST_IDS_QUERY_KEY)
      const ids = previous?.ids ?? []
      const liked = ids.includes(input.propertyId)
      const nextIds = liked ? ids.filter((id) => id !== input.propertyId) : [...ids, input.propertyId]
      queryClient.setQueryData(WISHLIST_IDS_QUERY_KEY, { ids: nextIds })
      return { previous }
    },
    onError: (err, _propertyId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WISHLIST_IDS_QUERY_KEY, context.previous)
      }
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : 'Could not update saved property.'
      toast.error(message)
    },
    onSuccess: (data, input) => {
      toast.success(data.liked ? 'Added to wishlist' : 'Removed from wishlist')
      trackPropertySaved({
        propertyId: input.propertyId,
        cityId: input.cityId,
        areaId: input.areaId,
        categoryId: input.categoryId,
        intent: input.intent,
        source: input.source ?? 'property_listing',
        requestSucceeded: true,
        likedAfter: data.liked,
      })
      queryClient.invalidateQueries({ queryKey: WISHLIST_LIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: WISHLIST_IDS_QUERY_KEY })
    },
  })
}
